/**
 * Workshop StageRail — right-edge Stage-Manager-style rail for running
 * subagent conversations.
 *
 * Production sources copied (strings only, never imported):
 * - WorkToastItem fields (stores/work-toast-store.ts): key/execution_id maps
 *   to conversationId here; state working|question|approval|blocked|done|
 *   failed, agentName, detail, and per-conversation switching are mirrored
 *   as props. Production toasts auto-hide (working 3s, done 10s), so users
 *   lose mid-process access — this rail is the persistent surface for that
 *   same data.
 * - Reveal interaction copied from FocusChromeRevealHandle
 *   (components/layout/focus-chrome-autohide.tsx:7-67): onPointerEnter /
 *   onFocus reveal, mirrored from the top/left edges to the RIGHT edge. No
 *   right-edge hover pattern exists in production.
 * - App icon is Icons + a colored tile (no brand assets); the logo-hover
 *   dock's one-avatar-per-agent tabs are intentionally not reused.
 *
 * Props-in, JSX-out. Local UI state only (revealed).
 */

import { useState, type FocusEvent } from "react";
import { surfaceRoleAttributes, surfaceRoleClassName } from "@/components/ui";
import { Icons, type IconName } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

type StageRailItemState =
  | "working"
  | "question"
  | "approval"
  | "blocked"
  | "done"
  | "failed";

interface StageRailItem {
  conversationId: string;
  agentName: string;
  appLabel: string;
  icon?: IconName;
  state: StageRailItemState;
  detail: string;
  nextAction?: string;
}

interface StageRailProps {
  items: StageRailItem[];
  activeId?: string;
  onSwitch?: (conversationId: string) => void;
  onFollowUp?: (conversationId: string) => void;
  className?: string;
  "data-testid"?: string;
}

const APP_TILES: Record<string, string> = {
  calendar: "bg-sky-400/15 text-sky-300",
  gmail: "bg-red-400/15 text-red-300",
  slack: "bg-purple-400/15 text-purple-300",
  code: "bg-accent/15 text-accent",
};

const FALLBACK_TILE = "bg-glass-fill-heavy text-secondary";

function tileClassName(appLabel: string): string {
  return APP_TILES[appLabel.trim().toLowerCase()] ?? FALLBACK_TILE;
}

interface StateMeta {
  label: string;
  dot: string;
  pulse: boolean;
}

const STATE_META: Record<StageRailItemState, StateMeta> = {
  working: { label: "Working", dot: "bg-accent", pulse: true },
  question: { label: "Needs you", dot: "bg-warning", pulse: false },
  approval: { label: "Approval", dot: "bg-warning", pulse: false },
  blocked: { label: "Blocked", dot: "bg-red-500", pulse: false },
  done: { label: "Done", dot: "bg-white/40", pulse: false },
  failed: { label: "Failed", dot: "bg-red-500", pulse: false },
};

function StatusDot({ state }: { state: StageRailItemState }) {
  const meta = STATE_META[state];
  return (
    <span
      className="relative flex h-2 w-2 shrink-0"
      role="img"
      aria-label={meta.label}
      data-testid="stage-rail-status-dot"
      data-state={state}
    >
      {meta.pulse && (
        <span
          aria-hidden="true"
          className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60"
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-flex h-2 w-2 rounded-full",
          meta.dot,
          meta.pulse && "animate-pulse",
        )}
      />
    </span>
  );
}

function AppTile({
  item,
  size,
}: {
  item: StageRailItem;
  size: "md" | "sm";
}) {
  const Icon = item.icon ? Icons[item.icon] : Icons.Bot;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center rounded-control",
        tileClassName(item.appLabel),
        size === "md" ? "h-8 w-8" : "h-7 w-7",
      )}
    >
      <Icon className={size === "md" ? "h-4 w-4" : "h-3.5 w-3.5"} />
    </span>
  );
}

function StageRailCard({
  item,
  isActive,
  tabbable,
  onSwitch,
  onFollowUp,
}: {
  item: StageRailItem;
  isActive: boolean;
  tabbable: boolean;
  onSwitch?: (conversationId: string) => void;
  onFollowUp?: (conversationId: string) => void;
}) {
  const meta = STATE_META[item.state];
  return (
    <button
      type="button"
      tabIndex={tabbable ? 0 : -1}
      aria-current={isActive ? "true" : undefined}
      data-testid="stage-rail-card-open"
      data-conversation-id={item.conversationId}
      onClick={(event) => {
        event.stopPropagation();
        onSwitch?.(item.conversationId);
      }}
      onPointerDown={(event) => event.stopPropagation()}
      className={cn(
        "nodrag group w-full rounded-preview border p-2.5 text-left transition-colors",
        isActive
          ? "border-accent/40 bg-accent/10"
          : "border-glass-stroke-subtle bg-glass-fill/40 hover:bg-glass-fill/60",
      )}
    >
      <span className="flex items-center gap-2">
        <AppTile item={item} size="md" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-primary">
            {item.agentName}
          </span>
          <span className="block truncate text-[11px] text-muted">
            {item.appLabel}
          </span>
        </span>
        <StatusDot state={item.state} />
        <span
          role="button"
          tabIndex={tabbable ? 0 : -1}
          aria-label={`Follow up on ${item.agentName}`}
          data-testid="stage-rail-followup-send"
          onClick={(event) => {
            event.stopPropagation();
            onFollowUp?.(item.conversationId);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onFollowUp?.(item.conversationId);
            }
          }}
          onPointerDown={(event) => event.stopPropagation()}
          className="nodrag rounded-control-sm p-1 text-muted opacity-0 transition-opacity hover:bg-glass-stroke-subtle hover:text-primary focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Icons.MessageSquare className="h-3.5 w-3.5" />
        </span>
      </span>
      <span className="mt-1.5 block line-clamp-2 text-xs leading-5 text-secondary">
        {item.detail}
      </span>
      {item.nextAction && (
        <span className="mt-1.5 flex items-center gap-1 text-[11px] leading-4 text-muted">
          <Icons.ArrowRight className="h-3 w-3 shrink-0" />
          <span className="truncate">
            <span className="sr-only">{`${meta.label}: `}</span>
            {item.nextAction}
          </span>
        </span>
      )}
    </button>
  );
}

export function StageRail({
  items,
  activeId,
  onSwitch,
  onFollowUp,
  className,
  "data-testid": testId = "stage-rail",
}: StageRailProps) {
  // Hover/focus reveal mirrored from FocusChromeRevealHandle to the right
  // edge: the ~14px hotzone plus the collapsed icon strip reveal the stack.
  const [revealed, setRevealed] = useState(false);

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setRevealed(false);
    }
  }

  return (
    <div
      data-testid={testId}
      onPointerEnter={() => setRevealed(true)}
      onFocus={() => setRevealed(true)}
      onBlur={handleBlur}
      onPointerLeave={() => setRevealed(false)}
      className={cn(
        "absolute inset-y-0 right-0 z-30 flex items-stretch justify-end",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center overflow-hidden transition-[width,opacity] duration-200 ease-out",
          revealed ? "w-72 opacity-100" : "w-0 opacity-0",
        )}
      >
        <div
          {...surfaceRoleAttributes("rail")}
          className={cn(
            surfaceRoleClassName("rail"),
            "nodrag flex max-h-[86%] w-72 shrink-0 flex-col gap-2 overflow-y-auto rounded-preview border border-glass-stroke-subtle p-2",
          )}
        >
          <p className="px-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
            Running{items.length > 0 ? ` · ${items.length}` : ""}
          </p>
          {items.length === 0 ? (
            <div
              data-testid="stage-rail-empty"
              className="rounded-preview border border-dashed border-glass-stroke-light bg-glass-fill/25 px-3 py-5 text-center"
            >
              <p className="text-xs font-medium text-secondary">
                No running conversations
              </p>
              <p className="mt-1 text-[11px] leading-4 text-muted">
                New agent work appears here.
              </p>
            </div>
          ) : (
            items.map((item) => (
              <StageRailCard
                key={item.conversationId}
                item={item}
                isActive={item.conversationId === activeId}
                tabbable={revealed}
                onSwitch={onSwitch}
                onFollowUp={onFollowUp}
              />
            ))
          )}
        </div>
      </div>

      <div
        data-testid="stage-rail-hotzone-reveal"
        onPointerEnter={() => setRevealed(true)}
        className="nodrag flex w-11 shrink-0 flex-col items-center gap-1.5 border-l border-glass-stroke-subtle bg-canvas/80 py-3"
      >
        <span
          aria-hidden="true"
          className="mb-1 h-14 w-1 rounded-full bg-glass-stroke-subtle"
        />
        {items.length === 0 ? (
          <span
            data-testid="stage-rail-strip-empty"
            className="grid h-7 w-7 place-items-center rounded-control text-muted"
          >
            <Icons.Sparkles className="h-3.5 w-3.5" />
          </span>
        ) : (
          items.map((item) => (
            <button
              key={item.conversationId}
              type="button"
              aria-label={`Switch to ${item.agentName}`}
              title={`${item.agentName} — ${STATE_META[item.state].label}`}
              data-testid="stage-rail-strip-open"
              data-conversation-id={item.conversationId}
              onClick={(event) => {
                event.stopPropagation();
                setRevealed(true);
                onSwitch?.(item.conversationId);
              }}
              onPointerDown={(event) => event.stopPropagation()}
              className={cn(
                "nodrag relative rounded-control p-0.5 transition-colors hover:bg-glass-stroke-subtle",
                item.conversationId === activeId &&
                  "ring-1 ring-accent/50 ring-offset-1 ring-offset-transparent",
              )}
            >
              <AppTile item={item} size="sm" />
              <span
                aria-hidden="true"
                className={cn(
                  "absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full ring-1 ring-black/40",
                  STATE_META[item.state].dot,
                  item.state === "working" && "animate-pulse",
                )}
              />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export type { StageRailItem, StageRailItemState, StageRailProps };
